<template>
  <div class="chat-container">
    <!-- Messages WITHOUT transition-group (Shadow DOM compatible) -->
    <div class="messages-wrapper">
      <!-- Contact Form (when needed) -->
      <div 
        v-if="showForm" 
        class="flex mb-4"
        :style="formAnimationStyle"
        ref="contactForm"
      >
        <!-- Agent Avatar -->
        <div
          class="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0 mr-3 overflow-hidden"
        >
          <img
            src="../../../assets/ChatlyAvatar.png"
            alt="NEX AI"
            class="w-full h-full object-cover"
          />
        </div>

        <!-- Form Message -->
        <div class="max-w-xs md:max-w-md">
          <div
            class="bg-white rounded-lg rounded-tl-none p-4"
            style="border: 1px solid #f0f0f0"
          >
            <p class="text-gray-800 text-sm mb-4">
              Bitte Gib deinen Namen und Email an damit wir starten können!
            </p>

            <!-- Show form inputs when not submitted -->
            <form
              v-if="!formSubmitted"
              @submit.prevent="submitForm"
              class="space-y-3"
            >
              <div>
                <input
                  type="text"
                  v-model="form.name"
                  class="w-full px-3 py-2 text-sm rounded-md focus:outline-none"
                  style="border: 1px solid #d1d5db"
                  placeholder="Dein Name"
                  required
                  :disabled="isSubmitting"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                />
              </div>

              <div>
                <input
                  type="email"
                  v-model="form.email"
                  class="w-full px-3 py-2 text-sm rounded-md focus:outline-none"
                  style="border: 1px solid #d1d5db"
                  placeholder="deine@email.com"
                  required
                  :disabled="isSubmitting"
                  @focus="handleInputFocus"
                  @blur="handleInputBlur"
                />
              </div>

              <div v-if="errorMessage" class="text-red-600 text-xs">
                {{ errorMessage }}
              </div>

              <!-- Submit Button with textColor support -->
              <button
                type="submit"
                class="w-full px-4 py-2 font-medium rounded-lg text-sm transition-all duration-200 hover:opacity-90"
                :style="{
                  backgroundColor: dynamicButtonColor,
                  color: styles.textColor || '#ffffff',
                  border: '1px solid rgba(0, 0, 0, 0.1)',
                }"
                :disabled="isSubmitting"
                :class="{ 'opacity-70': isSubmitting }"
              >
                <span
                  v-if="isSubmitting"
                  class="flex items-center justify-center space-x-2"
                >
                  <svg
                    class="animate-spin h-4 w-4"
                    :style="{ color: styles.textColor || '#ffffff' }"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      class="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      stroke-width="4"
                    ></circle>
                    <path
                      class="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  <span>Erstelle Chat...</span>
                </span>
                <span v-else>Chat Starten</span>
              </button>
            </form>

            <!-- Show thank you message when form is submitted -->
            <div
              v-else
              class="bg-green-100 rounded-lg p-4"
              style="border: 1px solid #e0f2e0"
            >
              <div class="flex items-center space-x-2">
                <svg
                  class="w-5 h-5 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M5 13l4 4L19 7"
                  ></path>
                </svg>
                <p class="text-green-800 text-sm">
                  Danke dir! Der Chat wird nun gestartet!
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Regular Messages with manual animation -->
      <div
        v-for="(message, index) in animatedMessages"
        :key="message.id || `message-${index}`"
        :style="message.animationStyle"
        :ref="`message-${index}`"
      >
        <!-- AI Message -->
        <div v-if="message.type === 'ai'" class="flex mb-4">
          <!-- Agent Avatar -->
          <div
            class="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0 mr-3 overflow-hidden"
          >
            <img
              src="../../../assets/ChatlyAvatar.png"
              alt="NEX AI"
              class="w-full h-full object-cover"
            />
          </div>

          <!-- AI Message Content -->
          <div class="max-w-xs md:max-w-md">
            <div
              class="bg-white rounded-lg rounded-tl-none p-4"
              style="border: 1px solid #f0f0f0"
            >
              <p class="text-gray-800 text-sm">{{ message.content }}</p>
            </div>
          </div>
        </div>

        <!-- User Message -->
        <div v-else-if="message.type === 'user'" class="flex mb-4 justify-end">
          <div class="max-w-xs md:max-w-md">
            <div
              class="rounded-lg rounded-tr-none p-4 text-sm"
              :style="{
                backgroundColor: dynamicButtonColor,
                color: styles.textColor || '#ffffff',
              }"
            >
              {{ message.content }}
            </div>
          </div>
        </div>

        <!-- Typing Indicator -->
        <div v-else-if="message.type === 'typing'" class="flex mb-4">
          <div
            class="w-8 h-8 rounded-full bg-blue-500 flex-shrink-0 mr-3 overflow-hidden"
          >
            <img
              src="../../../assets/ChatlyAvatar.png"
              alt="NEX AI"
              class="w-full h-full object-cover"
            />
          </div>

          <div class="max-w-xs md:max-w-md">
            <div
              class="bg-white rounded-lg rounded-tl-none p-4"
              style="border: 1px solid #f0f0f0"
            >
              <!-- Use your existing TypingIndicator component -->
              <TypingIndicator />
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  handleCustomerOnboarding,
  setCompanyId,
} from "../../../lib/chatAppwrite.js";
import { chatStateManager } from "../../../lib/chatStateManager";
import TypingIndicator from "../../TypingIndicator.vue";
export default {
  name: "ChatStart",
  components: {
    TypingIndicator
  },
  props: {
    messages: {
      type: Array,
      default: () => []
    },
    styles: {
      type: Object,
      default: () => ({
        buttonColor: null,
        bubbleColor: "#000000",
        textColor: "#ffffff",
      }),
    },
    companyId: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      showForm: false,
      formSubmitted: false,
      isSubmitting: false,
      errorMessage: "",
      form: {
        name: "",
        email: "",
      },
      hasInitialized: false,
      stateSubscription: null,
      actualCompanyId: null,
      isIntroInProgress: false,
      lastStateMessageCount: 0,
      
      // ✅ New animation data
      animatedMessages: [],
      formAnimationStyle: {
        opacity: 0,
        transform: 'translateY(15px) scale(0.95)',
        transition: 'all 0.4s ease-out'
      }
    };
  },
  computed: {
    dynamicButtonColor() {
      const color = this.styles.buttonColor || this.styles.bubbleColor || "#000000";
      return color;
    },
    focusRingColor() {
      return this.dynamicButtonColor;
    },
  },
  watch: {
    // ✅ Watch for message changes and animate them
    messages: {
      handler(newMessages, oldMessages) {
        this.animateNewMessages(newMessages, oldMessages || []);
      },
      deep: true
    },
    
    // ✅ Watch for form showing and animate it
    showForm: {
      handler(newVal) {
        if (newVal) {
          this.animateFormIn();
        }
      }
    },
    
    companyId: {
      handler(newVal, oldVal) {
        console.log("🔍 CHAT-START: companyId prop changed:", {
          from: oldVal,
          to: newVal,
          type: typeof newVal,
        });
        if (newVal && newVal !== this.actualCompanyId) {
          this.actualCompanyId = newVal;
          setCompanyId(newVal);
          console.log("✅ CHAT-START: Updated to new company ID:", newVal);
        }
      },
      immediate: true,
    },
  },
  mounted() {
    // Your existing mounted logic...
    console.log("🔍 CHAT-START: Mounted with props:", {
      companyId: this.companyId,
      companyIdType: typeof this.companyId,
      buttonColor: this.styles.buttonColor,
      bubbleColor: this.styles.bubbleColor,
      textColor: this.styles.textColor,
      dynamicButtonColor: this.dynamicButtonColor,
      allProps: this.$props,
    });

    this.actualCompanyId = this.companyId;

    if (this.actualCompanyId && this.actualCompanyId.trim()) {
      setCompanyId(this.actualCompanyId);
      console.log("✅ CHAT-START: Using REAL Company ID:", this.actualCompanyId);
    } else {
      console.error("❌ CHAT-START: No valid company ID provided!");
      this.errorMessage = "Configuration error: No company ID provided";
      return;
    }

    this.stateSubscription = chatStateManager.subscribe((newState) => {
      const currentMessageCount = newState.messages?.length || 0;
      
      // Your existing state management logic...
      const isTrueReset =
        currentMessageCount === 0 &&
        newState.currentPhase === "start" &&
        !newState.customer &&
        !newState.conversation &&
        this.lastStateMessageCount > 0 &&
        !this.isIntroInProgress;

      if (isTrueReset) {
        console.log("🔄 ChatStart: TRUE state reset detected - reinitializing component");
        this.resetComponentState();
      }

      this.lastStateMessageCount = currentMessageCount;
    });

    // Initialize animated messages
    this.animatedMessages = [...this.messages];
    this.checkAndStartIntro();
  },

  beforeUnmount() {
    if (this.stateSubscription) {
      this.stateSubscription();
    }
  },

  methods: {
    // ✅ Manual animation for new messages (Shadow DOM compatible)
    animateNewMessages(newMessages, oldMessages) {
      console.log("🎬 Animating new messages:", { newMessages, oldMessages });
      
      // Find truly new messages
      const newCount = newMessages.length;
      const oldCount = oldMessages.length;
      
      if (newCount > oldCount) {
        // Add new messages with initial hidden state
        const newMessagesToAdd = newMessages.slice(oldCount);
        
        newMessagesToAdd.forEach((message, index) => {
          const messageWithAnimation = {
            ...message,
            animationStyle: {
              opacity: 0,
              transform: 'translateY(15px) scale(0.95)',
              transition: 'all 0.4s ease-out'
            }
          };
          
          this.animatedMessages.push(messageWithAnimation);
          
          // Animate in after a small delay (staggered)
          setTimeout(() => {
            const messageIndex = this.animatedMessages.length - newMessagesToAdd.length + index;
            if (this.animatedMessages[messageIndex]) {
              this.animatedMessages[messageIndex].animationStyle = {
                opacity: 1,
                transform: 'translateY(0) scale(1)',
                transition: 'all 0.4s ease-out'
              };
            }
          }, 100 + (index * 100)); // Staggered delay
        });
      } else {
        // Handle message removal or reset
        this.animatedMessages = newMessages.map(message => ({
          ...message,
          animationStyle: {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            transition: 'all 0.4s ease-out'
          }
        }));
      }
    },
    
    // ✅ Animate form appearance
    animateFormIn() {
      this.$nextTick(() => {
        setTimeout(() => {
          this.formAnimationStyle = {
            opacity: 1,
            transform: 'translateY(0) scale(1)',
            transition: 'all 0.4s ease-out'
          };
        }, 100);
      });
    },

    // Your existing methods...
    handleInputFocus(event) {
      const input = event.target;
      input.style.borderColor = this.focusRingColor;
      input.style.boxShadow = `0 0 0 2px ${this.focusRingColor}40`;
    },

    handleInputBlur(event) {
      const input = event.target;
      input.style.borderColor = "#d1d5db";
      input.style.boxShadow = "none";
    },

    checkAndStartIntro() {
      // Your existing checkAndStartIntro logic...
      const chatState = chatStateManager.getState();
      const hasMessages = chatState.messages && chatState.messages.length > 0;
      
      if (chatState.customer && chatState.conversation) {
        console.log("✅ Active conversation detected - skipping intro completely");
        this.hasInitialized = true;
        return;
      }

      if (hasMessages && chatState.messages.length > 2) {
        console.log("✅ Existing conversation with messages detected - skipping intro");
        this.hasInitialized = true;
        return;
      }

      const hasIntroMessage = chatState.messages.some(
        (msg) =>
          msg.type === "ai" &&
          msg.content &&
          (msg.content.includes("NEX AI") || msg.content.includes("Persönlicher KI Assistent"))
      );

      if (hasIntroMessage) {
        console.log("✅ Intro message already exists in global state - skipping intro");
        this.hasInitialized = true;
        if (!chatState.customer && !this.showForm && !this.formSubmitted) {
          console.log("📱 No customer but intro exists - showing form");
          this.showForm = true;
        }
        return;
      }

      if (!hasIntroMessage && !this.hasInitialized && !chatState.customer && !chatState.conversation) {
        console.log("🎬 Starting fresh intro sequence - no intro found and no active conversation");
        this.hasInitialized = true;
        this.startIntroSequence();
        return;
      }

      console.log("ℹ️ ChatStart: No action needed - conversation already active");
    },

    resetComponentState() {
      console.log("🔄 ChatStart: Resetting component state for restart");
      const chatState = chatStateManager.getState();
      
      if (chatState.messages.length === 0) {
        console.log("🔄 Global state is empty - resetting component state");
        
        this.showForm = false;
        this.formSubmitted = false;
        this.isSubmitting = false;
        this.errorMessage = "";
        this.form = { name: "", email: "" };
        this.hasInitialized = false;
        this.animatedMessages = []; // Reset animated messages
        
        // Reset form animation
        this.formAnimationStyle = {
          opacity: 0,
          transform: 'translateY(15px) scale(0.95)',
          transition: 'all 0.4s ease-out'
        };

        this.$nextTick(() => {
          setTimeout(() => {
            console.log("🎬 ChatStart: Starting fresh intro after reset");
            this.checkAndStartIntro();
          }, 100);
        });
      }
    },

    startIntroSequence() {
      console.log("🎬 ChatStart: Starting intro sequence");
      this.isIntroInProgress = true;
      this.showForm = false;
      this.formSubmitted = false;

      chatStateManager.addTypingIndicator({ type: "ai" });

      setTimeout(() => {
        this.removeTypingMessage();
        this.$emit("add-message", {
          type: "ai",
          content: "Hey! Mein Name ist NEX AI, ich bin dein Persönlicher KI Assistent!",
        });

        setTimeout(() => {
          this.addContactForm();
        }, 800);
      }, 1500);
    },

    addContactForm() {
      console.log("🎬 ChatStart: Adding contact form");
      chatStateManager.addTypingIndicator({ type: "ai" });

      setTimeout(() => {
        chatStateManager.removeTypingMessage();
        setTimeout(() => {
          this.showForm = true;
          this.isIntroInProgress = false;
          console.log("📝 ChatStart: Contact form shown - intro sequence complete");
        }, 100);
      }, 1500);
    },

    removeTypingMessage() {
      chatStateManager.removeTypingMessage();
    },

    async submitForm() {
      // Your existing submitForm logic...
      if (!this.form.name.trim() || !this.form.email.trim()) {
        this.errorMessage = "Bitte alle Felder ausfüllen.";
        return;
      }

      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.form.email)) {
        this.errorMessage = "Bitte gültige Email-Adresse eingeben.";
        return;
      }

      if (!this.actualCompanyId || !this.actualCompanyId.trim()) {
        console.error("❌ SUBMIT: No company ID available for submission!");
        this.errorMessage = "Configuration error: Missing company ID";
        return;
      }

      this.errorMessage = "";
      this.isSubmitting = true;

      try {
        setCompanyId(this.actualCompanyId);
        const result = await handleCustomerOnboarding({
          name: this.form.name.trim(),
          email: this.form.email.trim(),
        });

        if (result.success) {
          console.log("✅ Customer onboarding successful:", result);
          this.formSubmitted = true;

          this.$emit("form-submitted", {
            formData: {
              name: this.form.name.trim(),
              email: this.form.email.trim(),
            },
            customer: result.customer,
            conversation: result.conversation,
            session: result.session,
            companyId: this.actualCompanyId,
          });

          setTimeout(() => {
            this.$emit("phase-complete", {
              phase: "start",
              nextPhase: "ai",
              customer: result.customer,
              conversation: result.conversation,
              customerName: this.form.name.trim(),
              companyId: this.actualCompanyId,
            });
          }, 500);

          console.log("🎉 Form submission complete, will transition to AI phase!");
        } else {
          this.errorMessage = result.error || "Ein Fehler ist aufgetreten. Bitte versuche es nochmal.";
          console.error("❌ Customer onboarding failed:", result.error);
        }
      } catch (error) {
        console.error("❌ Form submission error:", error);
        this.errorMessage = "Verbindungsfehler. Bitte versuche es nochmal.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* Chat Container */
.chat-container {
  height: 100%;
  overflow-y: auto;
  padding: 1rem;
  scroll-behavior: smooth;
}

.messages-wrapper {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}

/* Enhanced transition for inputs */
input {
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

/* Spinner animation for submit button */
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.animate-spin {
  animation: spin 1s linear infinite;
}
</style>